import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Bell, BellOff, Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function todayKey() {
  return DAY_KEYS[(new Date().getDay() + 6) % 7];
}

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", time: "07:00", pillar: "spirituality", days: [] });
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const firedRef = useRef(new Set());

  const load = async () => {
    try {
      const r = await axios.get(`${API}/reminders`);
      setReminders(r.data.reminders || []);
    } catch (e) {}
  };

  useEffect(() => {
    load();
  }, []);

  // Local tick: check every 30s. Fire browser notification when time matches.
  useEffect(() => {
    const check = () => {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const cur = `${hh}:${mm}`;
      const today = todayKey();
      reminders.forEach((r) => {
        if (!r.active) return;
        if (r.time !== cur) return;
        const inDays = !r.days || r.days.length === 0 || r.days.includes(today);
        if (!inDays) return;
        const fireKey = `${r.reminder_id}_${new Date().toDateString()}_${cur}`;
        if (firedRef.current.has(fireKey)) return;
        firedRef.current.add(fireKey);
        try {
          new Notification(`Ascend — ${r.title}`, {
            body: r.note || "Take 2 minutes. Come back to the path.",
            icon: "/favicon.ico",
          });
        } catch (e) {}
      });
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [reminders]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Notifications not supported here.");
      return;
    }
    const res = await Notification.requestPermission();
    setPermission(res);
    if (res === "granted") toast.success("Alarms enabled.");
  };

  const add = async () => {
    if (!form.title.trim()) return;
    try {
      const r = await axios.post(`${API}/reminders`, form);
      setReminders((rs) => [...rs, r.data]);
      setForm({ title: "", time: "07:00", pillar: "spirituality", days: [] });
      setAdding(false);
      toast.success("Alarm set.");
    } catch (e) {
      toast.error("Couldn't save alarm.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/reminders/${id}`);
      setReminders((rs) => rs.filter((r) => r.reminder_id !== id));
    } catch (e) {}
  };

  const toggle = async (r) => {
    try {
      await axios.patch(`${API}/reminders/${r.reminder_id}`, { active: !r.active });
      setReminders((rs) => rs.map((x) => (x.reminder_id === r.reminder_id ? { ...x, active: !x.active } : x)));
    } catch (e) {}
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d],
    }));
  };

  return (
    <section className="rounded-2xl bg-white border border-[#eadfc9] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-[#486a8f]" />
          <p className="font-mono-eyebrow text-[#486a8f]">Daily alarms</p>
        </div>
        {permission !== "granted" && (
          <button
            onClick={requestPermission}
            className="font-mono-eyebrow text-[10px] text-[#b94e4e] hover:underline"
          >
            Enable alerts
          </button>
        )}
      </div>

      <div className="space-y-2 mb-3">
        {reminders.length === 0 && !adding && (
          <p className="font-body text-[13px] text-[#6b7280]">
            No alarms yet. Set one for meditation, movement, reflection…
          </p>
        )}
        {reminders.map((r) => (
          <div
            key={r.reminder_id}
            className={`flex items-center gap-3 p-2.5 rounded-xl border ${
              r.active ? "bg-white border-[#eadfc9]" : "bg-[#faf6ec] border-[#f2ecdc] opacity-60"
            }`}
          >
            <button
              onClick={() => toggle(r)}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-[#e7edf5] text-[#486a8f] hover:bg-[#cdd8e8]"
            >
              {r.active ? <Bell size={13} /> : <BellOff size={13} />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-body text-[13px] font-medium text-[#0f1724]">{r.title}</p>
              <p className="font-body text-[11px] text-[#6b7280]">
                {r.time} · {r.days?.length ? r.days.join(", ") : "Every day"}
              </p>
            </div>
            <button
              onClick={() => remove(r.reminder_id)}
              className="text-[#9aa3ae] hover:text-[#b94e4e]"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="space-y-2.5 pt-2 border-t border-[#f2ecdc]">
          <Input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Alarm title (e.g., Morning meditation)"
            className="h-9 text-[13px]"
          />
          <div className="flex gap-2">
            <Input
              type="time"
              value={form.time}
              onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
              className="h-9 text-[13px] w-28"
            />
            <select
              value={form.pillar}
              onChange={(e) => setForm((f) => ({ ...f, pillar: e.target.value }))}
              className="flex-1 h-9 px-2 rounded-md border border-[#e6dcc6] bg-white text-[12px]"
            >
              <option value="financial">Financial</option>
              <option value="health">Health</option>
              <option value="spirituality">Spirituality</option>
              <option value="relationships">Relationships</option>
              <option value="endgame">Endgame</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DAY_KEYS.map((d, i) => (
              <button
                key={d}
                onClick={() => toggleDay(d)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  form.days.includes(d)
                    ? "bg-[#486a8f] text-white border-[#486a8f]"
                    : "bg-white text-[#6b7280] border-[#e6dcc6]"
                }`}
              >
                {DAYS[i]}
              </button>
            ))}
            <span className="font-body text-[10px] text-[#9aa3ae] self-center ml-1">
              {form.days.length === 0 ? "(every day)" : ""}
            </span>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)} className="rounded-full text-[#6b7280]">
              Cancel
            </Button>
            <Button size="sm" onClick={add} className="rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white">
              Set alarm
            </Button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[#faf6ec] border border-dashed border-[#d9cfb8] text-[#486a8f] font-body text-[12px] hover:bg-[#f2ecdc]"
        >
          <Plus size={13} /> Add alarm
        </button>
      )}
    </section>
  );
}
